/**
 * Utility functions for currency handling
 */

export const ZERO_DECIMAL_CURRENCIES = ['jpy', 'krw', 'vnd', 'clp', 'pyg', 'rwf', 'ugx', 'bif', 'djf', 'gnf', 'kmf', 'mga', 'vuv', 'xaf', 'xof', 'xpf'];

/**
 * Check if a currency is zero-decimal (e.g., JPY, KRW)
 * @param currency - Currency code (case-insensitive)
 */
export function isZeroDecimalCurrency(currency: string): boolean {
  return ZERO_DECIMAL_CURRENCIES.includes(currency.toLowerCase());
}

/**
 * Get the target currency code for a given locale
 */
export function getCurrencyForLocale(locale: string | null): string {
  if (!locale) return 'usd';
  const l = locale.toLowerCase();
  if (l === 'zh-tw' || l === 'zh-hk' || l === 'zh-mo') return 'twd';
  if (l === 'ja' || l === 'ja-jp') return 'jpy';
  if (l === 'ko' || l === 'ko-kr') return 'krw';
  return 'usd';
}

/**
 * Get the currency symbol for a given locale
 */
export function getCurrencySymbol(locale: string | null): string {
  if (!locale) return 'US$';
  const l = locale.toLowerCase();
  if (l === 'zh-tw' || l === 'zh-hk' || l === 'zh-mo') return 'NT$';
  if (l === 'ja' || l === 'ja-jp') return '¥';
  if (l === 'ko' || l === 'ko-kr') return '₩';
  return 'US$';
}

/**
 * Format a Stripe amount (usually in cents) to a whole unit if necessary.
 * For zero-decimal currencies like JPY, it remains the same.
 */
export function formatStripeAmount(amount: number, currency: string): number {
  if (isZeroDecimalCurrency(currency)) {
    return amount;
  }
  // Convert cents to whole units
  return Number((amount / 100).toFixed(2));
}

/**
 * Map currency symbol to ISO currency code
 */
export function getCurrencyFromSymbol(symbol: string): string {
  switch (symbol) {
    case 'NT$': return 'TWD';
    case '¥': return 'JPY';
    case '₩': return 'KRW';
    default: return 'USD';
  }
}
/**
 * Get the number of decimal places to show for a currency
 */
export function getDecimalCount(currencySymbol: string): number {
  const currency = getCurrencyFromSymbol(currencySymbol);
  const c = currency.toLowerCase();
  if (isZeroDecimalCurrency(c) || c === 'twd') {
    return 0;
  }
  return 2;
}
